import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { AppShell } from '@/components/layout/AppShell'
import { ErrorState } from '@/components/ui/ErrorState'
import { fetchMe, meKeys } from '@/lib/me-api'
import { GroepPage } from '@/pages/Groep'
import { GroepenPage } from '@/pages/Groepen'
import { InstellingenPage } from '@/pages/Instellingen'
import { LoginPage } from '@/pages/Login'
import { MedewerkerPage } from '@/pages/Medewerker'
import { RoosterPage } from '@/pages/Rooster'
import { TakenPage } from '@/pages/Taken'
import { TeamPage } from '@/pages/Team'
import { VakPage } from '@/pages/Vak'
import { VakkenPage } from '@/pages/Vakken'

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()()

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { fout?: string } => ({
    fout: typeof search.fout === 'string' ? search.fout : undefined,
  }),
  component: function LoginRoute() {
    const { fout } = loginRoute.useSearch()
    return <LoginPage fout={fout} />
  },
})

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: meKeys.me,
      queryFn: fetchMe,
    })
    if (!me) throw redirect({ to: '/login' })
    return { me }
  },
  component: AppShell,
})

const teamRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: TeamPage,
})

const medewerkerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/medewerker/$id',
  component: function MedewerkerRoute() {
    const { id } = medewerkerRoute.useParams()
    return <MedewerkerPage medewerkerId={id} />
  },
})

const groepenRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/groepen',
  component: GroepenPage,
})

const groepRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/groep/$code',
  component: function GroepRoute() {
    const { code } = groepRoute.useParams()
    return <GroepPage code={code} />
  },
})

const vakkenRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vakken',
  component: VakkenPage,
})

const vakRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/vak/$naam',
  component: function VakRoute() {
    const { naam } = vakRoute.useParams()
    return <VakPage naam={naam} />
  },
})

const takenRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/taken',
  component: TakenPage,
})

const instellingenRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/instellingen',
  component: InstellingenPage,
})

const roosterRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rooster',
  beforeLoad: ({ context }) => {
    if (context.me?.product !== 'upload') throw redirect({ to: '/' })
  },
  component: RoosterPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    teamRoute,
    medewerkerRoute,
    groepenRoute,
    groepRoute,
    vakkenRoute,
    vakRoute,
    takenRoute,
    instellingenRoute,
    roosterRoute,
  ]),
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultErrorComponent: ({ error, reset }) => <ErrorState error={error} onRetry={reset} />,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
